import dayjs from 'dayjs'
import { Dispatch, SetStateAction } from 'react'
import { FieldErrors } from 'react-hook-form'
import { useSnackbar } from 'notistack'
import { t } from '@lingui/macro'

import { defaultLpsInfos, getInsIdentityResultMessage, INS_OID } from '@/constants/dmp-connect'
import { useIsINSEnabled } from '@/shared/model/useIsINSEnabled'
import { useLazyGetInsFromIdentityInformationQuery } from '@/core/services/dmp-connect-js2'
import { useGetPatientQuery, useUpdatePatientMutation } from '@/core/services/patient'
import { PatientProfileFormData } from '@/features/patient/patient-profile-form'
import { appendAlert } from '@/redux/reducers/alerts.reducer'
import { selectDMPConnectTlsiConnectorId } from '@/redux/reducers/dmp-connect.reducer'
import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import { patientGenderToInsSex } from '@/utils/patient'

import { INFO_PATIENT_PAGE_ALERTS } from './constants'
import { isValidDate } from '@/utils/dateUtils'
import { useLogInsCall } from '@/pages/patient/model/useLogInsCall'
import { InsOperation } from '@/core/models/ins-audit.model'
import _ from 'lodash'

import { getInsSearchGivenCandidates, mergeInsiIdentityWithLocalBirthFirstName } from './ins-identity'

type UseSearchInsProps = {
  patientId: string
  setFormErrors: Dispatch<SetStateAction<FieldErrors<PatientProfileFormData>>>
}

export function useSearchIns({ patientId, setFormErrors }: UseSearchInsProps) {
  const { enqueueSnackbar } = useSnackbar()

  const dispatch = useAppDispatch()
  const tlsiConnectorId = useAppSelector(selectDMPConnectTlsiConnectorId)

  const { data: patient } = useGetPatientQuery(patientId)
  const { isInsCallBlocked } = useIsINSEnabled(patient)
  const [getInsFromIdentityInformation, { isFetching: isFetchingIns }] = useLazyGetInsFromIdentityInformationQuery()
  const [updatePatient] = useUpdatePatientMutation()
  const logInsCall = useLogInsCall()

  const searchIns = async () => {
    if (!patient || !patient.userInfo.birthday || !tlsiConnectorId) return
    if (isInsCallBlocked) {
      enqueueSnackbar(t`The INSi call is not possible for an identity flagged as Doubtful or Fictitious`, {
        variant: 'warning',
      })
      return
    }
    try {
      const LpsInfos = { ...defaultLpsInfos, s_instance: tlsiConnectorId }
      const s_given = patient.userInfo.birthFirstName
      const s_birthName = patient.userInfo.birthSurname || patient.userInfo.surname

      const regex = /^[a-zA-Z\-\' ]*$/
      const formErrors: FieldErrors<PatientProfileFormData> = {}

      if (!s_birthName || !regex.test(s_birthName)) {
        const field = patient.userInfo.birthSurname ? 'birthSurname' : 'surname'
        formErrors[field] = {
          message: t({
            id: 'Caratères autorisés: tiret (-), apostrophe (*), espace ( )',
            message: "Allowed characters: hyphen (-), apostrophe ('), space ( )",
          }),
          type: 'validate',
        }
      }
      if (!s_given?.trim()) {
        formErrors.birthFirstName = { message: t`This field is required`, type: 'required' }
      } else if (!regex.test(s_given)) {
        formErrors.birthFirstName = {
          message: t({
            id: 'Caratères autorisés: tiret (-), apostrophe (*), espace ( )',
            message: "Allowed characters: hyphen (-), apostrophe ('), space ( )",
          }),
          type: 'validate',
        }
      }
      if (Object.keys(formErrors).length) {
        setFormErrors(formErrors)
        return
      }

      if (!s_given) return

      const givenList = getInsSearchGivenCandidates(patient.userInfo)

      for (const given of givenList) {
        const { i_insIdentityResult, Identity } = await getInsFromIdentityInformation({
          LpsInfos,
          s_birthName: s_birthName.toUpperCase(),
          s_given: given.toUpperCase(),
          i_sex: patient.userInfo.gender ? patientGenderToInsSex(patient.userInfo.gender) : undefined,
          s_birthDate: dayjs(patient.userInfo.birthday).format('YYYY-MM-DD'),
          s_birthPlace: patient.userInfo?.birthPlaceCode,
        }).unwrap()

        const returnedIdentity =
          i_insIdentityResult === 2 && Identity
            ? {
              insNIR: Identity.Ins?.s_value && Identity.Ins?.s_key ? Identity.Ins.s_value + Identity.Ins.s_key : undefined,
              insOID: Identity.Ins?.s_oid,
              birthName: Identity.s_birthName,
              birthGiven: Identity.s_birthGiven,
              birthDate: Identity.s_birthDate,
              sex: Number.isInteger(Identity.i_sex) ? String(Identity.i_sex) : undefined,
              birthPlaceCode: Identity.s_birthPlace,
              insHistory: Identity.History?.map((history) => ({
                value: history.s_value,
                key: history.s_key,
                oid: history.s_oid,
                startDate: history.s_startDate,
                endDate: history.s_endDate,
              })),
            }
            : undefined

        await logInsCall({
          patient: patient._id.toString(),
          operation: InsOperation.GET_INS_WITHOUT_CARD,
          resultCode: i_insIdentityResult,
          returnedIdentity,
          returnStatus: returnedIdentity ? 'OK' : undefined,
        })

        if (i_insIdentityResult === 1) continue
        dispatch(appendAlert({ area: INFO_PATIENT_PAGE_ALERTS, alert: { message: getInsIdentityResultMessage(i_insIdentityResult), type: 'info' } }))

        if (Identity) {
          const errorMessages: string[] = []
          if (!isValidDate(Identity.s_birthDate)) {
            errorMessages.push(t`The birth date ${Identity.s_birthDate} is invalid for this patient`)
          }
          if (_.isEmpty(Identity.s_birthName)) {
            errorMessages.push(t`The birth name is empty for this patient`)
          }
          if (_.isEmpty(Identity.s_birthGiven)) {
            errorMessages.push(t`The birth given is empty for this patient`)
          }
          if (!Number.isInteger(Identity.i_sex)) {
            errorMessages.push(t`The sex is empty for this patient`)
          }
          if (errorMessages.length > 0) {
            enqueueSnackbar(errorMessages.join('\n'), { variant: 'error', style: { whiteSpace: 'pre-line' } })
            return
          }
          const identityMergeResult = mergeInsiIdentityWithLocalBirthFirstName(patient.userInfo, Identity)
          if (!identityMergeResult.success) {
            enqueueSnackbar(t`The first birth given name does not match the birth given names returned by INSi.`, { variant: 'error' })
            setFormErrors({
              birthFirstName: {
                message: t`The first birth given name must match the beginning of the birth given names.`,
                type: 'validate',
              },
            })
            return
          }

          await updatePatient({
            id: patient._id,
            userInfo: identityMergeResult.userInfo,
            insNIR: Identity.Ins.s_value + Identity.Ins.s_key,
            insOID: Identity.Ins.s_oid,
            insHistory: Identity.History?.map((history) => ({
              value: history.s_value,
              key: history.s_key,
              oid: history.s_oid,
              startDate: history.s_startDate,
              endDate: history.s_endDate,
            })),
          })
          if (Identity.Ins.s_oid === INS_OID['INS-NIA']) {
            setFormErrors({ insNIR: { message: t`Identifiant temporaire`, type: 'validate' } })
          }
          enqueueSnackbar(t`The patient information has been updated successfully.`, { variant: 'success' })
        }
        return
      }
      dispatch(appendAlert({ area: INFO_PATIENT_PAGE_ALERTS, alert: { message: getInsIdentityResultMessage(1), type: 'error' } }))
    } catch (error: any) {
      if (patient?._id) {
        await logInsCall({
          patient: patient._id.toString(),
          operation: InsOperation.GET_INS_WITHOUT_CARD,
        })
      }

      if ('error' in error && typeof error.error === 'string') {
        dispatch(
          appendAlert({
            area: INFO_PATIENT_PAGE_ALERTS,
            alert: { message: error.error, type: 'error' },
          })
        )
      }
    }
  }

  return { searchIns, isFetchingIns }
}
